import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard, clearSession } from "../api";

const CLOCK_LABELS = {
  delivery: "First delivery",
  refund_window: "Refund window",
  delivery_guarantee: "Delivery guarantee",
  quality_floor: "Quality floor",
};

function formatCountdown(deadlineIso) {
  const deadline = new Date(deadlineIso).getTime();
  const diffMs = deadline - Date.now();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const hours = Math.floor(abs / 3_600_000);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const label = days > 0 ? `${days}d ${remHours}h` : `${hours}h`;
  return past ? `${label} overdue` : `${label} left`;
}

function ClockCard({ clock }) {
  const label = CLOCK_LABELS[clock.clock_type] || clock.clock_type;
  const isOpen = clock.status === "pending" || clock.status === "warned";
  return (
    <div className="clock-card">
      <div className="clock-type">{label}</div>
      <span className={`status-pill status-${clock.status}`}>{clock.status}</span>
      <div className="muted" style={{ marginTop: 8 }}>
        {clock.status === "met" && clock.met_at && `Met ${new Date(clock.met_at).toLocaleDateString()}`}
        {clock.status === "stopped" && "No longer applicable"}
        {isOpen && clock.deadline_at && formatCountdown(clock.deadline_at)}
        {clock.status === "breached" && clock.deadline_at && `Deadline was ${new Date(clock.deadline_at).toLocaleDateString()}`}
      </div>
    </div>
  );
}

function BrandDnaSnapshot({ intake }) {
  if (!intake) {
    return <p className="empty-state">No Brand DNA intake on file yet.</p>;
  }
  const raw = intake.raw || {};
  const rows = [
    ["Industry", raw.industry],
    ["City", raw.business_city],
    ["Country", raw.business_country],
    ["Tone", raw.tone],
    ["Platforms", Array.isArray(raw.platforms) ? raw.platforms.join(", ") : raw.platforms],
    ["Marketing goal", raw.marketing_goal],
  ].filter(([, value]) => value);

  return (
    <div className="intake-card">
      <dl>
        {rows.map(([term, value]) => (
          <React.Fragment key={term}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p className="muted" style={{ marginTop: 12 }}>
        Submitted {intake.submitted_at ? new Date(intake.submitted_at).toLocaleDateString() : "—"}
        {intake.synthesized ? " · AI-synthesized" : " · raw"}
      </p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboard()
      .then(setData)
      .catch((err) => {
        if (/401|session/i.test(err.message)) {
          clearSession();
          navigate("/", { replace: true });
          return;
        }
        setError(err.message || "Could not load your dashboard.");
      });
  }, [navigate]);

  function handleLogout() {
    clearSession();
    navigate("/", { replace: true });
  }

  if (error) {
    return (
      <div className="page">
        <div className="card">
          <h1>Something went wrong</h1>
          <div className="alert alert-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <p className="muted">Loading your dashboard…</p>
      </div>
    );
  }

  const { client, sla_clocks: slaClocks, latest_intake: latestIntake } = data;

  return (
    <div className="page-wide">
      <div className="header-row">
        <div>
          <h1>{client.business_name}</h1>
          <span className="plan-badge">{client.plan || "No plan on file"}</span>
        </div>
        <button className="btn-link logout-link" onClick={handleLogout}>Sign out</button>
      </div>

      <div className="section-title">SLA status</div>
      {slaClocks.length === 0 ? (
        <p className="empty-state">No SLA clocks yet — these start once your Brand DNA intake is submitted.</p>
      ) : (
        <div className="clock-grid">
          {slaClocks.map((clock) => (
            <ClockCard key={clock.clock_type} clock={clock} />
          ))}
        </div>
      )}

      <div className="section-title">Brand DNA snapshot</div>
      <BrandDnaSnapshot intake={latestIntake} />
    </div>
  );
}
